import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { getIngredientNames } from "@/lib/api";
import { editDistance } from "@/lib/editDistance";
import { cn } from "@/lib/utils";
import { Plus, X } from "lucide-react";

type Row = { key: number; name: string; strength: string };

let nextKey = 0;
const blank = (): Row => ({ key: nextKey++, name: "", strength: "" });

const titleCase = (name: string) => name.replace(/\b[a-z]/g, c => c.toUpperCase());

/** "Amoxycillin (500mg) + Clavulanic Acid 125 mg" → rows; reads the catalog's format and a typed one */
function parse(composition: string): Row[] {
  return composition.split("+").map(s => s.trim()).filter(Boolean).map(part => {
    const m = part.match(/^(.*?)\s*\(([^)]*)\)\s*$/) ?? part.match(/^(.*?)\s+(\d.*)$/);
    return { ...blank(), name: (m ? m[1] : part).trim(), strength: (m ? m[2] : "").trim() };
  });
}

/** Rows → "Amoxycillin (500 mg) + Clavulanic Acid (125 mg)", the catalog's format */
const join = (rows: Row[]) =>
  rows.filter(r => r.name.trim())
    .map(r => (r.strength.trim() ? `${r.name.trim()} (${r.strength.trim()})` : r.name.trim()))
    .join(" + ");

/** Edit distance from what is typed to the start of a name ("amoxi" → "amoxy"cillin, "cefal" → "cephal"exin) */
const prefixDistance = (q: string, name: string) =>
  Math.min(...[q.length - 1, q.length, q.length + 1].map(l => editDistance(q, name.slice(0, l))));

/**
 * Ingredients matching what is typed: starts with it, then starts with a near spelling (the catalog
 * writes "amoxycillin", "cephalexin"), then a later word starts with it, then contains it.
 * `names` comes most common first, so each group keeps that order.
 */
function suggest(names: string[], typed: string): string[] {
  const q = typed.toLowerCase();
  const max = q.length >= 8 ? 2 : q.length >= 4 ? 1 : 0;
  const starts: string[] = [], near: string[] = [], wordStarts: string[] = [], contains: string[] = [];
  for (const n of names) {
    if (n === q) return [];   // already a full ingredient name
    if (n.startsWith(q)) starts.push(n);
    else if (max && prefixDistance(q, n) <= max) near.push(n);
    else if (n.includes(` ${q}`)) wordStarts.push(n);
    else if (q.length >= 3 && n.includes(q)) contains.push(n);
  }
  return [...starts, ...near, ...wordStarts, ...contains].slice(0, 8);
}

interface Props {
  id: string;
  value: string;
  onChange: (composition: string) => void;
  /** Enter on an empty ingredient — the doctor is finished */
  onDone?: () => void;
  autoFocus?: boolean;
}

/**
 * A medicine's composition as one row per ingredient: name (suggested from the catalog's ingredients,
 * the most common first) and strength. No "+" to type, which sits on the second keyboard on a tablet.
 */
export default function CompositionEditor({ id, value, onChange, onDone, autoFocus }: Props) {
  const [rows, setRows] = useState<Row[]>(() => { const r = parse(value); return r.length ? r : [blank()]; });
  const [names, setNames] = useState<string[]>([]);
  const [focusKey, setFocusKey] = useState<number | null>(null);
  const nameRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const strengthRefs = useRef<Record<number, HTMLInputElement | null>>({});

  useEffect(() => { getIngredientNames().then(setNames); }, []);
  // A row just added: put the cursor in its ingredient
  useEffect(() => {
    if (focusKey == null) return;
    nameRefs.current[focusKey]?.focus();
    setFocusKey(null);
  }, [focusKey]);

  const update = (next: Row[]) => { setRows(next); onChange(join(next)); };
  const set = (key: number, changes: Partial<Row>) => update(rows.map(r => (r.key === key ? { ...r, ...changes } : r)));
  const add = () => { const r = blank(); update([...rows, r]); setFocusKey(r.key); };
  const remove = (key: number) => update(rows.length > 1 ? rows.filter(r => r.key !== key) : [blank()]);

  const printed = join(rows);

  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-[minmax(0,1fr)_112px_32px] gap-x-2 text-xs font-medium text-label" aria-hidden>
        <span>Ingredient</span><span>Strength</span>
      </div>
      {rows.map((r, i) => (
        <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_112px_32px] gap-x-2 items-center">
          <IngredientInput
            id={i === 0 ? id : undefined}
            inputRef={el => { nameRefs.current[r.key] = el; }}
            value={r.name}
            names={names}
            autoFocus={autoFocus && i === 0 && !r.name}
            onChange={name => set(r.key, { name })}
            onPick={name => { set(r.key, { name }); strengthRefs.current[r.key]?.focus(); }}
            onEnter={() => (r.name.trim() ? strengthRefs.current[r.key]?.focus() : onDone?.())}
            label={`Ingredient ${i + 1}`}
          />
          <Input
            ref={el => { strengthRefs.current[r.key] = el; }}
            value={r.strength}
            onChange={e => set(r.key, { strength: e.target.value })}
            onKeyDown={e => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              // Next ingredient: the following row, or a new one after the last
              const next = rows[i + 1];
              if (next) nameRefs.current[next.key]?.focus();
              else if (r.name.trim()) add();
              else onDone?.();
            }}
            placeholder="e.g. 500 mg"
            aria-label={`Strength ${i + 1}`}
            className="h-9 text-sm font-mono placeholder:font-sans"
          />
          <button type="button" onClick={() => remove(r.key)}
            aria-label={`Remove ingredient ${i + 1}`}
            className="touch-target h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
        className="inline-flex items-center gap-1 h-7 text-xs font-medium text-primary hover:underline">
        <Plus className="w-3.5 h-3.5" />Add ingredient
      </button>
      <p className="text-xs text-muted-foreground break-words">
        {printed ? <>Prints as <span className="text-foreground">{printed}</span></> : "Prints as the generic name, under the brand."}
      </p>
    </div>
  );
}

/** One ingredient name, with the catalog's matching ingredients listed under it */
function IngredientInput({ id, inputRef, value, names, autoFocus, onChange, onPick, onEnter, label }: {
  id?: string;
  inputRef: (el: HTMLInputElement | null) => void;
  value: string;
  names: string[];
  autoFocus?: boolean;
  onChange: (value: string) => void;
  onPick: (name: string) => void;
  onEnter: () => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const typed = value.trim();
  const suggestions = useMemo(() => (open && typed.length >= 2 ? suggest(names, typed) : []), [open, names, typed]);
  const listId = `${label.replace(/\s/g, "-").toLowerCase()}-list`;

  useEffect(() => { setHighlighted(0); }, [typed]);

  const pick = (name: string) => { onPick(titleCase(name)); setOpen(false); };

  return (
    <div className="relative min-w-0">
      <Input
        id={id}
        ref={inputRef}
        value={value}
        autoFocus={autoFocus}
        autoComplete="off"
        role="combobox"
        aria-label={label}
        aria-expanded={suggestions.length > 0}
        aria-controls={listId}
        placeholder="e.g. Paracetamol"
        className="h-9 text-sm"
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={e => {
          if (suggestions.length && e.key === "ArrowDown") { e.preventDefault(); setHighlighted(i => Math.min(i + 1, suggestions.length - 1)); }
          else if (suggestions.length && e.key === "ArrowUp") { e.preventDefault(); setHighlighted(i => Math.max(i - 1, 0)); }
          else if (e.key === "Enter") {
            e.preventDefault();
            if (suggestions.length) pick(suggestions[highlighted]);
            else onEnter();
          }
        }}
      />
      {suggestions.length > 0 && (
        <div id={listId} role="listbox"
          className="absolute z-50 mt-1 w-full min-w-[200px] max-h-56 overflow-auto rounded-md border border-border bg-popover py-1 shadow-md">
          {suggestions.map((n, i) => (
            <div key={n} role="option" aria-selected={i === highlighted}
              onMouseDown={e => { e.preventDefault(); pick(n); }}
              onMouseEnter={() => setHighlighted(i)}
              className={cn("px-3 py-1.5 text-sm cursor-pointer", i === highlighted && "bg-accent")}>
              {titleCase(n)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
